import { useEffect, useRef, memo, useState } from 'react';

const PRICE_CHART_ID = 'price-chart-widget-container';

function PriceChartWidget({ tokenAddress }) {
  const container = useRef(null);
  const [status, setStatus] = useState('loading');
  const scriptLoaded = useRef(false); // Track if script has loaded

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentContainer = container.current;

    const loadWidget = () => {
      if (typeof window.createMyWidget === 'function') {
        // Clear previous widget content before reinitializing
        if (currentContainer) {
          currentContainer.innerHTML = '';
        }
        window.createMyWidget(PRICE_CHART_ID, {
          autoSize: true,
          chainId: 'solana',
          tokenAddress: tokenAddress,
          defaultInterval: '1',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Etc/UTC',
          theme: 'dark',
          locale: 'en',
          backgroundColor: '#071321',
          gridColor: '#0d2035',
          textColor: '#68738D',
          candleUpColor: '#4CE666',
          candleDownColor: '#E64C4C',
          hideLeftToolbar: false,
          hideTopToolbar: false,
          hideBottomToolbar: false
        });
        setStatus('loaded');
      } else {
        console.error('createMyWidget function is not defined.');
        setStatus('error');
      }
    };

    const scriptId = 'moralis-chart-widget';
    let script = document.getElementById(scriptId);

    const initializeScript = () => {
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://moralis.com/static/embed/chart.js';
        script.type = 'text/javascript';
        script.async = true;
        script.onload = () => {
          scriptLoaded.current = true;
          loadWidget();
        };
        script.onerror = () => {
          console.error('Failed to load the Moralis chart widget script.');
          setStatus('error');
        };
        document.body.appendChild(script);
      } else if (scriptLoaded.current) {
        loadWidget(); // Only call if script is fully loaded
      } else {
        // Wait for script to load if it’s already in DOM but not ready
        const checkScript = setInterval(() => {
          if (typeof window.createMyWidget === 'function') {
            clearInterval(checkScript);
            scriptLoaded.current = true;
            loadWidget();
          }
        }, 100);
        return () => clearInterval(checkScript);
      }
    };

    setStatus('loading'); // Reset status on tokenAddress change
    initializeScript();

    // Cleanup only on component unmount, not on tokenAddress change
    return () => {
      if (currentContainer && status !== 'loaded') {
        currentContainer.innerHTML = ''; // Clear only if not loaded
      }
    };
  }, [tokenAddress, status]);

  // Full cleanup on unmount
  useEffect(() => {
    return () => {
      const script = document.getElementById('moralis-chart-widget');
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
      scriptLoaded.current = false; // Reset script state
    };
  }, []); // Empty dependency array for unmount only

  return (
    <div
      className="mx-auto flex justify-center items-center rounded-lg w-[95%] sm:h-[70vh] h-[70vh] sm:p-4 p-0 bg-slate-800/90 dark:bg-pink-200/90 backdrop-blur-md"
    >
      {status === 'loading' && <div className="text-white">Loading chart...</div>}
      {status === 'error' && <div className="text-red-500">Error loading chart</div>}
      {status === 'loaded' && (
        <div id={PRICE_CHART_ID} ref={container} style={{ width: '100%', height: '100%' }} />
      )}
    </div>
  );
}

export const TradingViewC = memo(PriceChartWidget);